import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';

import { CustomCollapseCard, CustomImage, CustomModal, Text, useText } from '@src/components';

import { Affix, Button } from 'antd';

import dayjs from '@src/utils/day';
import { moneyFormat } from '@src/utils/utils';
import { useMediaQuery } from '@src/hooks/useMediaQuery';

import {
  Cancel,
  finishPaymentProcess,
  processPayment,
  setCreditCardMatchVous,
  setPayLaterDayForThanksPage,
  setSelectStoredCard,
} from '@src/store/slice/mobilExpressSlice';
import { instructionAdd, nextStep } from '@src/store/slice/autoUploadInstructionsSlice';

import PayWithNewCardAutoUpload from '@src/pages/ticketRestaurant/paymentModals/payWithNewCardAutoUpload/payWithNewCardAutoUpload';
import PayWithNewCard from '@src/pages/ticketRestaurant/paymentModals/payWithNewCard/payWithNewCard';

import modalClose from '@src/assets/icons/icon-close.svg';
import logoVisaResponsive from '@src/assets/images/cardOrders/visa_responsive.png';
import logoAmericanExpressResponsive from '@src/assets/images/cardOrders/american_express_responsive.png';
import masterCardLogoResponsive from '@src/assets/images/cardOrders/mastercard_responsive.png';
import logoMobilExpressResponsive from '@src/assets/images/cardOrders/logo_mobilexpress-responsive.png';

import '@src/styles/autoUploadInstruction/paymentType.scss';

let paymentOrderDueDate = null;
let _selectedStoredCard = null;

const OrderSummary = ({ form, formVisible }) => {
  const isMobil = useMediaQuery({ query: '(max-width: 1199.98px)' });
  const threeDSecurity = useText('3dSecurity');

  const dispatch = useDispatch();
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);
  const { totalAutoLoading, integrationId, autoLoadingId } = useSelector(
    (state) => state?.autoUploadInstructions,
  );
  const { selectedStoredCard, paymentType, payWithLinkinEmail } = useSelector(
    (state) => state?.mobilExpress,
  );

  const [threeDSecureContent, setThreeDSecureContent] = useState('');

  const [isValid, setIsValid] = useState(false);
  const afterMount = false;
  const isPayDayToday = false;
  const [payWhenBalanceActiveDate, setPayWhenBalanceActiveDate] = useState(false);

  const [saveCardBody, setSaveCardBody] = useState({ body: {}, checked: false });
  const [modals, setModals] = useState({
    threeDSecureModalVisible: false,
    instantRemittanceModalVisible: false,
    payWithNewCardAutoUpload: false,
    payWithNewCard: false,
  });

  const modalAction = (prop, value) => {
    setModals((modals) => ({
      ...modals,
      [prop]: value,
    }));
  };

  useEffect(() => {
    _selectedStoredCard = selectedStoredCard;
  }, [selectedStoredCard]);

  const creditCardPayment = useCallback(async () => {
    const body = {
      autoLoadingCreateRequestDto: {
        periodType: totalAutoLoading?.periodType,
        confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
        startDay: totalAutoLoading?.startDay,
        orderProcessType: totalAutoLoading?.orderProcessType,
        monthChoice: totalAutoLoading?.chooseMonth,
        cardToken: _selectedStoredCard,
        paymentType: totalAutoLoading?.paymentType,
      },
    };

    if (integrationId && integrationId > 0) {
      body.integrationId = integrationId;
    }

    if (autoLoadingId && autoLoadingId > 0) {
      body.autoLoadingId = totalAutoLoading?.id;
    }

    const action = await dispatch(instructionAdd(body));

    if (instructionAdd.fulfilled.match(action)) {
      dispatch(nextStep());
    }
  }, [dispatch, selectedStoredCard, totalAutoLoading, integrationId, autoLoadingId]);

  const finishOrder = async () => {
    await creditCardPayment();
  };

  const paymentLater = async () => {
    modalAction('payWithNewCard', false);
    await finishOrder();
  };

  useEffect(() => {
    window.paymentLater = paymentLater;
    return () => {
      window.paymentLater = () => {};
    };
  }, [paymentLater]);

  const eftPayment = useCallback(async () => {
    const body = {
      autoLoadingCreateRequestDto: {
        periodType: totalAutoLoading?.periodType,
        confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
        startDay: totalAutoLoading?.startDay,
        orderProcessType: totalAutoLoading?.orderProcessType,
        monthChoice: totalAutoLoading?.chooseMonth,
        paymentType: totalAutoLoading?.paymentType,
      },
    };

    if (integrationId && integrationId > 0) {
      body.integrationId = integrationId;
    }

    if (autoLoadingId && autoLoadingId > 0) {
      body.autoLoadingId = totalAutoLoading?.id;
    }

    const action = await dispatch(instructionAdd(body));

    if (instructionAdd.fulfilled.match(action)) {
      dispatch(nextStep());
    }
  }, [dispatch, totalAutoLoading, integrationId, autoLoadingId]);

  const handlePayment = useCallback(async () => {
    closeAllModals();

    if (formVisible) {
      const values = await form.validateFields();
      const { cardSaveChecked } = values;

      if (isPayDayToday || !afterMount) {
        await saveNewCard();
      } else if (afterMount) {
        if (cardSaveChecked) {
          await saveNewCardPayLater();
        } else {
          modalAction('payWithNewCard', true);
        }
      }
    } else if (paymentType === 1) {
      return creditCardPayment();
    } else if (paymentType === 2) {
      return eftPayment();
    }
  }, [dispatch, form, formVisible, paymentType]);

  const customerId = selectedCustomer?.customerId;

  const isVerifiedPayment = useCallback(async () => {
    document?.activeElement?.blur();
    const values = await form.getFieldsValue();
    const { cardSaveChecked } = values;

    if (formVisible && cardSaveChecked === false) {
      modalAction('payWithNewCardAutoUpload', true);
      return null;
    }

    if (afterMount) {
      if (formVisible && cardSaveChecked === true) {
        modalAction('payWithNewCard', true);
        await form.validateFields();
      }
      paymentOrderDueDate = dayjs(currentLoadBalance?.activationDate)
        .utc(true)
        .utcOffset(180)
        .toISOString();
      await dispatch(setPayLaterDayForThanksPage(paymentOrderDueDate));
      if (!formVisible) {
        await paymentLater();
        return;
      }
    }
    if (payWithLinkinEmail) {
      await paymentLater();
      return;
    }
    await handlePayment();
  }, [
    afterMount,
    currentLoadBalance,
    dispatch,
    form,
    formVisible,
    handlePayment,
    payWithLinkinEmail,
  ]);

  const closeAllModals = () => {
    modalAction('threeDSecureModalVisible', false);
    modalAction('instantRemittanceModalVisible', false);
    modalAction('payWithNewCard', false);
    modalAction('payWithNewCardAutoUpload', false);
  };

  const setPaymentOrderDueDate = (value) => (paymentOrderDueDate = value);

  const saveNewCardWithParam = async (price, isDump) => {
    const customerId = selectedCustomer?.customerId;
    const values = await form.validateFields();
    const body = {
      body: {
        currency: 'TRY',
        paymentInstrument: 'NewCard',
        paymentInstrumentInfo: {
          newCard: {
            processType: 'preauth',
            use3DSecure: true,
            cvv: values?.cvv,
            cardInfo: {
              cardHolderName: values?.cardHolderName,
              cardNumber: values?.cardNumber?.replaceAll('-', ''),
              lastMonth: values?.lastMonth,
              lastYear: values?.lastYear,
            },
          },
        },
        returnUrl: `${process.env.VITE_SITE_URL}/payment-result.html?paymentInstrument=NewCard${
          isDump ? '&PaymentReferenceType=preauth' : ''
        }`,
        totalAmount: price,
        customerInfo: { customerId: customerId?.toString() },
      },
      paymentReferenceType: 3,
      referenceId: '1',
    };
    return dispatch(processPayment(body));
  };

  const saveNewCardPayLater = async () => {
    const action = await saveNewCardWithParam(1, true);
    const values = await form.validateFields();

    if (processPayment.fulfilled.match(action)) {
      const data = action?.payload?.data;

      if (data?.result === 'Success') {
        await handleOrderFinish();
        return;
      }

      modalAction('payWithNewCard', false);
      modalAction('payWithNewCardAutoUpload', false);

      setThreeDSecureContent(
        'data:text/html;charset=utf-8,' + encodeURIComponent(action?.payload?.data?.redirectForm),
      );

      modalAction('threeDSecureModalVisible', true);

      setSaveCardBody({ body: values, checked: true });
    }
  };

  const saveNewCard = async () => {
    modalAction('payWithNewCard', false);
    const values = await form.validateFields();

    try {
      const action = await saveNewCardWithParam(totalAutoLoading?.totalPrice, false);

      if (processPayment.fulfilled.match(action)) {
        const data = action?.payload?.data;
        if (data?.result === 'Success') {
          await handleOrderFinish();
          return;
        }

        setThreeDSecureContent(
          'data:text/html;charset=utf-8,' + encodeURIComponent(action?.payload?.data?.redirectForm),
        );

        modalAction('threeDSecureModalVisible', true);

        if (values.cardSaveChecked) {
          setSaveCardBody({ body: values, checked: true });
        }
      }
    } catch (e) {}
  };

  useEffect(async () => {
    const values = await form?.getFieldsValue();

    const cardNumber = values?.cardNumber?.replaceAll('_', '').replaceAll('-', '');
    const cardNumberLength = cardNumber?.length;
    const cardNumberIsValid = cardNumberLength === 16;

    const cardHolderName = values?.cardHolderName;
    const cardHolderNameIsValid = cardHolderName?.length > 0;

    const lastMonth = values?.lastMonth;
    const lastMonthIsValid = lastMonth > 0;

    const lastYear = values?.lastYear;
    const lastYearIsValid = lastYear?.length > 0;

    const cvv = values?.cvv?.replaceAll('_', '');
    const cvvIsValid = cvv?.length > 2;

    setIsValid(
      cardNumberIsValid &&
        cardHolderNameIsValid &&
        lastMonthIsValid &&
        lastYearIsValid &&
        cvvIsValid,
    );
  });

  useEffect(() => {
    window.handlePayment = isVerifiedPayment;
    return () => {
      window.handlePayment = () => {};
    };
  }, [isVerifiedPayment]);

  const payWhenBalanceActiveDay = async () => {
    paymentOrderDueDate = dayjs(currentLoadBalance?.activationDate)
      .utc(true)
      .utcOffset(180)
      .toISOString();
    await dispatch(setPayLaterDayForThanksPage(paymentOrderDueDate));
    if (formVisible) {
      const values = await form.validateFields();
      const { cardSaveChecked } = values;
      setPayWhenBalanceActiveDate(true);
      if (cardSaveChecked) {
        await saveNewCardPayLater();
      } else {
        modalAction('payWithNewCard', true);
      }
    } else {
      await paymentLater();
    }
  };

  useEffect(() => {
    window.payWhenBalanceActiveDay = payWhenBalanceActiveDay;
    return () => {
      window.payWhenBalanceActiveDay = () => {};
    };
  }, [payWhenBalanceActiveDay]);

  const handleOrderFinish = useCallback(async () => {
    await finishOrder();
  }, [totalAutoLoading, dispatch]);

  const finishPaymentProcessHandlerFunc = useCallback(
    async (body) => {
      try {
        const action = await dispatch(finishPaymentProcess(body));
        if (finishPaymentProcess.fulfilled.match(action)) {
          if (body.paymentReferenceType === 3) {
            await dispatch(
              Cancel({
                body: {
                  orderId: body.body.orderId,
                  paymentInstrument: 'newCard',
                  transactionReason: 'Ön Provizyon İptal',
                },
              }),
            );
          }
          if (saveCardBody.checked) {
            const body = {
              saveCardBody: {
                customerInfo: {
                  customerId: customerId?.toString(),
                },
                cardHolterName: saveCardBody?.body?.cardHolderName,
                cardNumber: saveCardBody?.body?.cardNumber?.replaceAll('-', ''),
                cardMonth: saveCardBody?.body?.lastMonth,
                cardYear: saveCardBody?.body?.lastYear,
                selectedVous: [
                  {
                    vouId: 1,
                    vouName: 'TICKET RESTAURANT',
                  },
                ],
              },
              creditCartVouMatchList: [
                {
                  customerId: customerId?.toString(),
                  vouId: 1,
                  provisionDay: 0,
                  type: 0,
                },
              ],
            };
            const newCardResponse = await dispatch(setCreditCardMatchVous(body));
            if (setCreditCardMatchVous.fulfilled.match(newCardResponse)) {
              const newCardToken = newCardResponse?.payload?.data?.cardToken;
              await dispatch(setSelectStoredCard(newCardToken));
              _selectedStoredCard = newCardToken;
              paymentOrderDueDate = true;
            }
          }
          if (formVisible) {
            if (payWhenBalanceActiveDate) {
              setPayWhenBalanceActiveDate(false);
              await paymentLater();
              return;
            }
            form.resetFields();
          }

          await handleOrderFinish();
        } else {
          modalAction('threeDSecureModalVisible', false);
        }
      } catch (e) {}
    },
    [dispatch, form, formVisible, handleOrderFinish, saveCardBody?.body, saveCardBody.checked],
  );

  const iframeOnLoad = useCallback(
    (queryString) => {
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        const paymentInstrument = urlParams.get('paymentInstrument');
        const orderId = urlParams.get('OrderId');
        const systemTransId = urlParams.get('SystemTransId');
        const totalAmount = urlParams.get('TotalAmount');
        const paymentReferenceType = urlParams.get('PaymentReferenceType');

        const isPreAuth = paymentReferenceType === 'preauth';

        if (paymentInstrument && orderId && systemTransId) {
          const body = {
            paymentReferenceType: 3,
            referenceId: totalAutoLoading?.id,
            body: {
              orderId,
              merchantProcessId: 'IslemId33',
              systemTransId: systemTransId,
              paymentInstrument: paymentInstrument,
              paymentInstrumentInfo: {},
              totalAmount: +totalAmount,
            },
          };

          if (paymentInstrument === 'NewCard') {
            body.body.paymentInstrumentInfo = {
              newCard: {},
            };
          } else {
            body.body.paymentInstrumentInfo = {
              storedCard: { otp: '', cvv: '' },
            };
          }

          if (isPreAuth) {
            body.paymentReferenceType = 3;
            body.referenceId = '1';
            body.body.paymentInstrumentInfo = {
              newCard: {},
            };
          }
          (async () => {
            await finishPaymentProcessHandlerFunc(body);
          })();
        } else {
          modalAction('threeDSecureModalVisible', false);
        }
      }
    },
    [totalAutoLoading?.id, finishPaymentProcessHandlerFunc],
  );

  useEffect(() => {
    window.finishPaymentProcessHandler = iframeOnLoad;
    window.modalAction = modalAction;
    return () => {
      window.finishPaymentProcessHandler = () => {};
      window.modalAction = () => {};
    };
  }, [iframeOnLoad]);

  const isFormVisible = formVisible ? !isValid : !selectedStoredCard;

  const isDisabled = paymentType === 1 ? isFormVisible : false;

  return (
    <div className="payment-order-summary-content">
      <CustomCollapseCard bodyCenter cardTitle={<Text t="instructionSummary" />}>
        <div className="payment-order-summary-container">
          <div className="payment-order-summary-list">
            <span className="summary-title"><Text t="orderType" />:</span>
            <p>{totalAutoLoading.orderProcessType === 2 ? <Text t="classic" /> : 'Excel'}</p>

            <span className="summary-title"><Text t="autoActivationDate" />:</span>
            <p><Text t="ofEachMonth" /> {totalAutoLoading?.startDay}<Text t="day2" /></p>

            <span className="summary-title">
              <Text t="approvalRequest" />
            </span>
            <p className="address-label">
              {totalAutoLoading?.confirmEveryMonth === 0
                ? <Text t="iDonTWantToApproveEveryMonth" />
                : <Text t="iWantToApproveEveryMonth" />}
            </p>
          </div>

          <div className="summary-info-box">
            <span className="summary-title">
              <Text t="numberOfCards3" />
            </span>
            <p>{totalAutoLoading?.totalCount}</p>
          </div>

          <div className="summary-total-price-info-box">
            <span className="summary-title">
              <Text t="totalBalanceLoad" />
            </span>
            <p>{moneyFormat(totalAutoLoading?.totalPrice, true)}</p>
          </div>

          <div className="summary-total-price-info-box">
            <span className="summary-title">
              <Text t="totalAmountToBePaid" />
            </span>
            <p>{moneyFormat(totalAutoLoading?.totalPaymentPrice, true)}</p>
          </div>

          <span className="approval-text">
            <Text t="iApproveTheOrderAndPaymentDetailedAbove" />
          </span>

          {!isMobil && (
            <Button
              id="balanceUpload-payment-orderSummary-desktop-payment-btn"
              disabled={formVisible ? false : isDisabled}
              className="payment-btn"
              onClick={isVerifiedPayment}
            >
              <Text t="continue" />
            </Button>
          )}
        </div>
      </CustomCollapseCard>

      {totalAutoLoading?.paymentType !== 5 && isMobil && (
        <div className="footer-logo">
          <div className="left-logos">
            <CustomImage className="logoMobil" src={logoMobilExpressResponsive} />
          </div>
          <span className={'pay-text'}>
            <Text t="yourCardsAreStoredSecurelyInThePci" />
          </span>
          <div className="right-logos">
            <CustomImage src={logoAmericanExpressResponsive} />
            <CustomImage src={logoVisaResponsive} />
            <CustomImage src={masterCardLogoResponsive} />
          </div>
        </div>
      )}

      {isMobil &&
        createPortal(
          <Affix offsetBottom={0}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#fff',
                padding: '18px 0',
              }}
            >
              <Button
                id="balanceUpload-payment-orderSummary-mobil-payment-btn"
                disabled={formVisible ? false : isDisabled}
                className="payment-btn"
                onClick={isVerifiedPayment}
              >
                <Text t="continue" />
              </Button>
            </div>
          </Affix>,
          document.body,
        )}

      {modals.threeDSecureModalVisible && (
        <CustomModal
          width={500}
          maskClosable={false}
          footer={false}
          title={threeDSecurity}
          visible={modals.threeDSecureModalVisible}
          onCancel={() => modalAction('threeDSecureModalVisible', false)}
          closeIcon={<CustomImage src={modalClose} />}
        >
          <embed src={threeDSecureContent} width="100%" height="400" />
        </CustomModal>
      )}

      {modals.instantRemittanceModalVisible && (
        <CustomModal
          width={500}
          maskClosable={false}
          footer={false}
          title={'Anında Havale'}
          visible={''}
          onCancel={() => modalAction('instantRemittanceModalVisible', false)}
          closeIcon={<CustomImage src={modalClose} />}
        >
          <embed src={''} width="100%" height="400" />
        </CustomModal>
      )}

      {modals.payWithNewCard && (
        <PayWithNewCard
          setModalVisible={(value) => modalAction('payWithNewCard', value)}
          saveAction={saveNewCardPayLater}
          paymentLater={paymentLater}
          orderDate={currentLoadBalance?.activationDate}
          setPaymentOrderDueDate={setPaymentOrderDueDate}
          formVisible={formVisible}
          form={form}
        />
      )}

      {modals.payWithNewCardAutoUpload && (
        <PayWithNewCardAutoUpload
          setModalVisible={(value) => modalAction('payWithNewCardAutoUpload', value)}
          saveAction={saveNewCardPayLater}
          form={form}
        />
      )}
    </div>
  );
};

export default OrderSummary;
